import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, ArrowUpRight,
  CreditCard, Signal, Tv, ScanSearch, Package, Gift, Handshake, Scale,
  Eye, Target, AlertOctagon, MapPin,
  PanelTopOpen, Zap, Smartphone, LayoutGrid, ListVideo,
  Film, Play, RotateCcw, ListChecks, Check, X, BookOpenText, Languages, FastForward,
  TrendingUp, Compass, Bird,
  Bus, Hourglass, Layers, Coffee,
  Sparkles, Search,
  Tag, Megaphone, Globe,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import useReveal from '../hooks/useReveal';
import { projects } from '../mock';
import introDrawerImg from '../assets/shots/intro-drawer.jpg';
import shortcutImg from '../assets/shots/shortcut.jpg';
import bottomNavImg from '../assets/shots/bottom-nav.jpg';
import genreCardImg from '../assets/shots/genre-card.jpg';
import homepageRailImg from '../assets/shots/homepage-rail.jpg';
import landingPageImg from '../assets/shots/landing-page.png';
import detailDrawerImg from '../assets/shots/detail-drawer.png';
import epDrawerImg from '../assets/shots/ep-drawer.png';
import langDrawerImg from '../assets/shots/lang-drawer.png';
import videoPlayerImg from '../assets/shots/video-player.png';
import endOfSeriesImg from '../assets/shots/end-of-series.png';
import fullPlayerImg from '../assets/shots/full-player.png';
import adInPlayerImg from '../assets/shots/ad-in-player.png';

const SECTIONS = ['hero', 'challenge', 'priorities', 'framework', 'models', 'landing', 'continuity', 'fragments', 'outcome', 'explore-next'];
const SECTION_LABELS = ['Intro', 'Challenge', 'Priorities', 'Framework', 'Models', 'Landing', 'Continuity', 'Fragments', 'Outcome', 'Next'];

export default function ShotsCaseStudy() {
  useReveal();
  const project = useMemo(() => projects.find((p) => p.slug === 'shots-tataplay'), []);
  const nextProject = useMemo(() => {
    const idx = projects.findIndex((p) => p.slug === 'shots-tataplay');
    return projects[(idx + 1) % projects.length];
  }, []);

  const [progress, setProgress] = useState(0);
  const [activeIdx, setActiveIdx] = useState(0);
  const [entryIdx, setEntryIdx] = useState(0);
  const [entryAuto, setEntryAuto] = useState(true);
  const entryAutoRef = useRef(null);
  const [playerIdx, setPlayerIdx] = useState(0);
  const [funnelIdx, setFunnelIdx] = useState(0);
  const funnelTimerRef = useRef(null);
  const [landingIdx, setLandingIdx] = useState(0);
  const [landingAuto, setLandingAuto] = useState(true);
  const landingAutoRef = useRef(null);
  const landingScrollRef = useRef(null);
  const landingTabsRef = useRef(null);
  const [phoneHeight, setPhoneHeight] = useState(0);

  const ENTRY_POINTS = useMemo(() => [
    { n: '01', Icon: PanelTopOpen, title: 'Intro drawer', sub: 'First-time modal on app open',         image: introDrawerImg },
    { n: '02', Icon: Zap,          title: 'Shortcut',     sub: 'Persistent quick-access tile',         image: shortcutImg },
    { n: '03', Icon: Smartphone,   title: 'Bottom nav',   sub: 'Promoted into primary navigation',     image: bottomNavImg },
    { n: '04', Icon: LayoutGrid,   title: 'Genre card',   sub: 'Inside Browse by Genre',               image: genreCardImg },
    { n: '05', Icon: ListVideo,    title: 'Homepage rail',sub: 'Dedicated content rail on home',       image: homepageRailImg },
  ], []);

  const PLAYER_TABS = useMemo(() => [
    { n: '01', Icon: Play,         title: 'Video player',                sub: 'Full-bleed vertical player — swipe between episodes',          image: videoPlayerImg },
    { n: '02', Icon: BookOpenText, title: 'Details drawer',              sub: 'Synopsis & episode list — quick context without leaving',     image: detailDrawerImg },
    { n: '03', Icon: ListChecks,   title: 'Episode drawer',              sub: 'Jump between episodes inside the player',                     image: epDrawerImg },
    { n: '04', Icon: Languages,    title: 'Language drawer',             sub: 'Switch language without exiting playback',                    image: langDrawerImg },
    { n: '05', Icon: FastForward,  title: 'End-of-series suggestion',    sub: '"Ready for another quick watch?" — next pick surfaced inline', image: endOfSeriesImg },
  ], []);

  // Auto-cycle funnel highlight every 15s
  useEffect(() => {
    funnelTimerRef.current = setInterval(() => {
      setFunnelIdx((i) => (i + 1) % 4);
    }, 15000);
    return () => clearInterval(funnelTimerRef.current);
  }, []);

  const LANDING_TABS = useMemo(() => [
    { n: '01', Icon: Tv,          title: 'Hero banner carousel',                       sub: 'A held title or sponsored placement, setting tone before any personalisation begins.',                          dynamic: false, scroll: 0.00 },
    { n: '02', Icon: Sparkles,    title: 'Start your first story / Continue watching', sub: 'The line that does the heavy lifting — an invitation for first-timers, a pick-up for returning viewers.',         dynamic: true,  scroll: 0.14 },
    { n: '03', Icon: Languages,   title: 'Shots in your language',                     sub: 'A filter, not a recommendation — narrow the page without ever leaving it.',                                       dynamic: false, scroll: 0.22 },
    { n: '04', Icon: Hourglass,   title: 'Finish under 30 minutes',                    sub: '"Do I actually have time for this right now?" — the silent question every microdrama viewer is asking.',         dynamic: false, scroll: 0.27 },
    { n: '05', Icon: LayoutGrid,  title: 'Browse by genre',                            sub: 'A wide, low-commitment door into the catalogue for anyone who\u2019d rather explore than be told what to watch.', dynamic: false, scroll: 0.34 },
    { n: '06', Icon: TrendingUp,  title: 'Trending Shots near you',                    sub: 'Where the page starts paying attention — what\u2019s trending shifts by location and language.',                   dynamic: true,  scroll: 0.43 },
    { n: '07', Icon: Target,      title: 'Recommended Shots',                          sub: 'A first, lighter pass from the recommendation engine — early signal, not a full profile yet.',                     dynamic: true,  scroll: 0.60 },
    { n: '08', Icon: Compass,     title: 'Shots for you',                              sub: 'The page\u2019s final word — a doorway into the full recommendation feed, with language and genre filters layered on top.', dynamic: true,  scroll: 0.85 },
  ], []);

  // Scroll the landing phone to a section
  const scrollLandingTo = (i) => {
    const el = landingScrollRef.current;
    if (!el) return;
    const target = el.scrollHeight * LANDING_TABS[i].scroll;
    const max = el.scrollHeight - el.clientHeight;
    el.scrollTo({ top: Math.max(0, Math.min(target, max)), behavior: 'smooth' });
  };

  // Auto-rotate landing tabs every 30s until user clicks
  useEffect(() => {
    if (!landingAuto) return undefined;
    landingAutoRef.current = setInterval(() => {
      setLandingIdx((i) => {
        const next = (i + 1) % LANDING_TABS.length;
        return next;
      });
    }, 30000);
    return () => clearInterval(landingAutoRef.current);
  }, [landingAuto, LANDING_TABS.length]);

  // Trigger scroll whenever landingIdx changes
  useEffect(() => {
    scrollLandingTo(landingIdx);
  }, [landingIdx]);

  // Match phone height to tabs column height
  useEffect(() => {
    const el = landingTabsRef.current;
    if (!el) return undefined;
    const update = () => setPhoneHeight(el.offsetHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);

  const selectLanding = (i) => {
    setLandingAuto(false);
    setLandingIdx(i);
  };

  // Auto-rotate entry points every 10s (until user clicks)
  useEffect(() => {
    if (!entryAuto) return undefined;
    entryAutoRef.current = setInterval(() => {
      setEntryIdx((i) => (i + 1) % ENTRY_POINTS.length);
    }, 10000);
    return () => clearInterval(entryAutoRef.current);
  }, [entryAuto, ENTRY_POINTS.length]);

  const selectEntry = (i) => {
    setEntryAuto(false);
    setEntryIdx(i);
  };

  useEffect(() => { window.scrollTo({ top: 0 }); }, []);
  useEffect(() => {
    const onScroll = () => {
      const total = document.body.scrollHeight - window.innerHeight;
      setProgress(total > 0 ? Math.min(100, Math.round((window.scrollY / total) * 100)) : 0);
      let active = 0;
      SECTIONS.forEach((id, i) => {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top < window.innerHeight * 0.5) active = i;
      });
      setActiveIdx(active);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (i) => {
    const el = document.getElementById(SECTIONS[i]);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (!project) return null;

  return (
    <div className="App">
      <Navbar />

      <div className="fixed top-0 left-0 h-[3px] z-[60] transition-[width] duration-100" style={{ width: `${progress}%`, background: 'var(--terracotta)' }} />

      <div className="hidden lg:flex fixed right-6 top-1/2 -translate-y-1/2 z-40 flex-col gap-3">
        {SECTIONS.map((_, i) => (
          <button key={i} onClick={() => scrollTo(i)} title={SECTION_LABELS[i]} className="group flex items-center gap-3" aria-label={SECTION_LABELS[i]}>
            <span className="text-[10px] tracking-[0.18em] uppercase opacity-0 group-hover:opacity-100 transition-opacity duration-200" style={{ color: 'var(--ink-soft)' }}>
              {SECTION_LABELS[i]}
            </span>
            <span className="rounded-full transition-all duration-300 block" style={{ width: activeIdx === i ? 12 : 8, height: activeIdx === i ? 12 : 8, background: activeIdx === i ? 'var(--terracotta)' : 'var(--border)' }} />
          </button>
        ))}
      </div>

      <article className="pt-28 md:pt-32 pb-20">
        <div className="max-w-3xl mx-auto px-6">
          <Link to="/#work" className="inline-flex items-center gap-2 text-[12px] tracking-[0.14em] uppercase link-underline" style={{ color: 'var(--terracotta-deep)' }}>
            <ArrowLeft size={14} /> Back to Work
          </Link>
        </div>

        {/* HERO */}
        <section id="hero" className="max-w-3xl mx-auto px-6 pt-12 pb-16 mb-12" style={{ borderBottom: '1px solid var(--border)' }}>
          <p className="reveal text-[11px] tracking-[0.22em] uppercase mb-5" style={{ color: 'var(--terracotta)' }}>
            Case study · Tata Play · TPMA
          </p>
          <h1 className="reveal reveal-delay-1 font-display leading-[1.05] mb-6" style={{ fontSize: 'clamp(40px, 6vw, 64px)', color: 'var(--ink)', fontWeight: 600 }}>
            Shots: a <em className="italic" style={{ color: 'var(--terracotta)' }}>new habit</em>, inside an old one
          </h1>
          <p className="reveal reveal-delay-2 text-[16px] leading-[1.75] mb-8 max-w-[580px]" style={{ color: 'var(--ink-soft)' }}>
            Shots was Tata Play&apos;s entry into microdramas — short, vertically consumed, episodic stories built for mobile-first viewing. The
            format was completely new to the ecosystem. The challenge wasn&apos;t launching a feature. It was helping users discover, understand,
            and adopt a new viewing behaviour inside an app they already thought they understood.
          </p>
          <div className="reveal reveal-delay-3 flex flex-wrap gap-2.5 mb-5">
            <MetaPill accent>Assistant Manager - UI/UX</MetaPill>
            <MetaPill>End-to-end</MetaPill>
          </div>
          <p className="reveal reveal-delay-3 text-[12.5px] tracking-[0.08em]" style={{ color: 'var(--ink-soft)' }}>
            <strong className="font-semibold" style={{ color: 'var(--ink)' }}>Focus:</strong> Discovery · Merchandising · Player · Engagement
          </p>
        </section>

        {/* Cover gradient */}
        <div className="max-w-6xl mx-auto px-6 mb-16">
          <div className="reveal rounded-[22px] overflow-hidden shadow-warm-lg relative" style={{ aspectRatio: '16/9', background: project.gradient }}>
            {project.cover && <img src={project.cover} alt={project.title} className="absolute inset-0 w-full h-full object-cover" />}
          </div>
        </div>

        {/* 01 — CHALLENGE */}
        <Chapter id="challenge" number="01 — The challenge">
          <H2>Users already knew <em className="italic" style={{ color: 'var(--terracotta)' }}>what TPMA was for.</em></H2>
          <P>When people opened the Tata Play Mobile App, they came with a fixed set of intents.</P>
          <div className="reveal grid grid-cols-1 md:grid-cols-3 gap-3 my-7">
            <IntentCard Icon={CreditCard} title="Manage account" />
            <IntentCard Icon={Signal}     title="Recharge" />
            <IntentCard Icon={Tv}         title="Browse Live TV" />
          </div>
          <P>
            That familiarity was the obstacle. Shots wasn&apos;t a new feature slotting into an established category — it was a new{' '}
            <em className="italic">behaviour</em> being introduced inside a mental model that had no room for it yet.
          </P>
          <PullQuote eyebrow="The real question">
            &ldquo;How do you help users discover a new habit without disrupting the behaviours that already bring them to the app?&rdquo;
          </PullQuote>
        </Chapter>

        {/* 02 — PRIORITIES */}
        <Chapter id="priorities" number="02 — Competing priorities">
          <H2>Visibility was the obvious answer. <em className="italic" style={{ color: 'var(--terracotta)' }}>It was also the wrong one.</em></H2>
          <P>
            Put Shots everywhere. Promote it aggressively. Drive traffic. Except homepage real estate was already one of the most contested
            surfaces in the product — and every existing surface was already earning its place.
          </P>

          <p className="reveal text-[11px] tracking-[0.22em] uppercase mb-3 mt-7" style={{ color: 'var(--ink-soft)' }}>
            Surfaces already pulling their weight
          </p>
          <div className="reveal grid grid-cols-2 md:grid-cols-5 gap-2.5 mb-7">
            <SurfaceCard Icon={CreditCard} title="Account management" />
            <SurfaceCard Icon={ScanSearch} title="Content discovery" />
            <SurfaceCard Icon={Package}    title="Subscriptions" />
            <SurfaceCard Icon={Gift}       title="Promotions" />
            <SurfaceCard Icon={Handshake}  title="Partner offerings" />
          </div>
          <div className="reveal flex items-center justify-center mb-4">
            <span className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'var(--terracotta)', color: '#FAF6EE' }}>
              <Scale size={18} />
            </span>
          </div>
          <p className="reveal text-[15.5px] leading-[1.8] mb-4 text-center max-w-[640px] mx-auto" style={{ color: 'var(--ink-soft)' }}>
            Maximum visibility for Shots could easily come at the cost of existing journeys. This wasn&apos;t just a design problem —{' '}
            <strong className="font-semibold" style={{ color: 'var(--ink)' }}>it was a prioritisation problem</strong>, and it needed Product,
            Content, and Business aligned before a single screen got drawn.
          </p>
        </Chapter>

        {/* 03 — FRAMEWORK */}
        <Chapter id="framework" number="03 — A discovery framework">
          <H2>Three questions, <em className="italic" style={{ color: 'var(--terracotta)' }}>not three placements.</em></H2>
          <P>To align stakeholders with different goals, discovery got framed around three principles instead of a list of surfaces.</P>

          <div className="reveal grid grid-cols-1 md:grid-cols-3 gap-3 my-8">
            <PrincipleCard Icon={Eye}           title="Visibility" sub="Can users notice it?" />
            <PrincipleCard Icon={Target}        title="Relevance"  sub="Does it appear where it makes sense?" />
            <PrincipleCard Icon={AlertOctagon}  title="Disruption" sub="Does it interrupt an existing task?" />
          </div>

          {/* From → To */}
          <div className="reveal grid grid-cols-[1fr_auto_1fr] gap-4 items-stretch my-8">
            <FromToBox eyebrow="From" text='"Where can we place Shots?"' tone="muted" />
            <div className="self-center text-center" style={{ color: 'var(--terracotta)' }}>
              <ArrowRight size={22} />
            </div>
            <FromToBox eyebrow="To" text='"Where can users discover Shots naturally?"' tone="accent" />
          </div>
          <P className="reveal">This shifted the conversation from promotion to experience design.</P>
        </Chapter>

        {/* 04 — DISCOVERY MODELS */}
        <Chapter id="models" number="04 — Exploring discovery models">
          <H2>Three options. <em className="italic" style={{ color: 'var(--terracotta)' }}>One clear winner.</em></H2>
          <div className="space-y-4 my-7">
            <OptionCard
              n="Option 1"
              Icon={Tag}
              title="Dedicated Shots destination"
              pros={['Clear ownership', 'High visibility']}
              cons={['Relies on intentional discovery', 'Requires learning a new destination']}
            />
            <OptionCard
              n="Option 2"
              Icon={Megaphone}
              title="Promotional-first"
              pros={['Immediate exposure', 'Easy to implement']}
              cons={['Risks banner blindness', 'Feels promotional, not contextual']}
            />
            <OptionCard
              n="Option 3"
              tag="Preferred"
              Icon={Globe}
              title="Distributed discovery"
              pros={['Lower friction', 'Higher contextual relevance', 'No new navigation behaviour']}
              cons={['Requires touchpoints across the app']}
              winner
            />
          </div>
          <P>
            Rather than one entry point, we designed a <em className="italic">network</em> of discovery opportunities — moments where users were
            already exploring content, where Shots could surface naturally instead of interrupting.
          </P>

          <IllustrationBox icon={MapPin} label="Discovery entry points — click a tab to view the screen, or watch the auto-carousel">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_300px] gap-7 items-start">
              {/* LEFT — tabs grid + caption */}
              <div>
                <div className="grid grid-cols-2 gap-3">
                  {ENTRY_POINTS.map((e, i) => {
                    const isOn = entryIdx === i;
                    const isLast = i === ENTRY_POINTS.length - 1;
                    return (
                      <button
                        key={e.n}
                        onClick={() => selectEntry(i)}
                        className={`flex flex-col items-start justify-between gap-3 rounded-[14px] p-4 transition-all duration-200 text-left aspect-square ${isLast ? 'col-span-2 aspect-auto min-h-[88px] flex-row items-center' : ''}`}
                        style={{
                          background: isOn ? 'var(--terracotta)' : 'var(--bg)',
                          color: isOn ? '#FAF6EE' : 'var(--ink)',
                          border: `1.5px solid ${isOn ? 'var(--terracotta)' : 'var(--border)'}`,
                          boxShadow: isOn ? '0 10px 24px -10px rgba(196,98,45,0.5)' : 'none',
                        }}
                      >
                        {isLast ? (
                          <>
                            <e.Icon size={32} style={{ color: isOn ? '#FAF6EE' : 'var(--terracotta)' }} />
                            <div className="flex-1">
                              <span className="block text-[10px] tracking-[0.22em] uppercase mb-1" style={{ color: isOn ? '#FADDCC' : 'var(--sage)' }}>{e.n}</span>
                              <span className="block text-[14px] font-medium leading-snug">{e.title}</span>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="flex items-center justify-between w-full">
                              <span className="text-[10px] tracking-[0.22em] uppercase" style={{ color: isOn ? '#FADDCC' : 'var(--sage)' }}>{e.n}</span>
                              <e.Icon size={32} />
                            </div>
                            <span className="text-[14px] font-medium leading-snug">{e.title}</span>
                          </>
                        )}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[12px] mt-5 leading-[1.55] italic" style={{ color: 'var(--ink-soft)' }}>
                  {ENTRY_POINTS[entryIdx].sub}
                  {entryAuto && <span className="ml-2 text-[10px] tracking-[0.18em] uppercase" style={{ color: 'var(--terracotta)' }}>● auto</span>}
                </p>
              </div>

              {/* RIGHT — phone mockup (360x805 aspect) */}
              <div className="flex justify-center md:justify-end">
                <div
                  className="overflow-hidden rounded-[22px] shadow-warm w-full max-w-[260px]"
                  style={{ aspectRatio: '360 / 805', background: '#0F0A14', border: '1.5px solid var(--ink)' }}
                >
                  <img
                    key={ENTRY_POINTS[entryIdx].image}
                    src={ENTRY_POINTS[entryIdx].image}
                    alt={ENTRY_POINTS[entryIdx].title}
                    className="block w-full h-full object-cover transition-opacity duration-500"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>

            {/* Progress dots */}
            <div className="flex items-center justify-center gap-1.5 mt-6">
              {ENTRY_POINTS.map((_, i) => (
                <button
                  key={i}
                  onClick={() => selectEntry(i)}
                  aria-label={`Go to ${ENTRY_POINTS[i].title}`}
                  className="h-1.5 rounded-full transition-all duration-300"
                  style={{
                    width: entryIdx === i ? 26 : 7,
                    background: entryIdx === i ? 'var(--terracotta)' : 'var(--border)',
                  }}
                />
              ))}
            </div>
          </IllustrationBox>

          <HighlightQuote eyebrow="The user's only question">
            &ldquo;Is this worth the next few minutes of my time?&rdquo;
          </HighlightQuote>
          <P>
            That&apos;s the only question a microdrama needs to answer fast. So the discovery surfaces leaned on visual storytelling, episodic
            cues, and concise metadata — reducing the decision to something almost instant.
          </P>
        </Chapter>

        {/* 05 — LANDING PAGE (Storytelling) */}
        <Chapter id="landing" number="05 — Where Shots actually lived">
          <H2>One page. <em className="italic" style={{ color: 'var(--terracotta)' }}>A different opening line for everyone.</em></H2>
          <P>
            Every entry point we&apos;d designed — the intro drawer, the shortcut, the bottom nav tab, the genre card, the homepage rail — led
            somewhere. That somewhere had to do two jobs at once: feel instantly orienting to someone who&apos;d never seen a microdrama in
            their life, and feel like a returning ritual to someone three episodes deep into a story.
          </P>
          <P>
            Rather than building two separate pages, we built one page that knows how to greet you differently. Most of it stays put. The
            greeting changes.
          </P>

          <HighlightQuote eyebrow="Design principle">
            &ldquo;The page had to behave like it remembered you — without ever feeling like it had to be rebuilt for you.&rdquo;
          </HighlightQuote>

          <P>
            The page reads top to bottom like a conversation that gets gradually more specific. It opens wide — a hero carousel anyone would
            see, language filters anyone could use, a genre grid anyone could browse. By the time you&apos;ve scrolled past the midpoint,
            it&apos;s narrowed in on what&apos;s trending near you, what the recommendation engine thinks you&apos;d like, and a doorway into
            a fully filtered feed built around your taste.
          </P>

          {/* Legend */}
          <div className="reveal flex flex-wrap items-center gap-5 mt-6 mb-2">
            <div className="flex items-center gap-2 text-[11.5px] tracking-[0.04em]" style={{ color: 'var(--ink-soft)' }}>
              <span className="inline-block w-3 h-3 rounded-full" style={{ background: 'var(--surface)', border: '1.5px solid var(--border)' }} />
              Same for everyone
            </div>
            <div className="flex items-center gap-2 text-[11.5px] tracking-[0.04em]" style={{ color: 'var(--ink-soft)' }}>
              <span className="inline-block w-3 h-3 rounded-full" style={{ background: 'var(--terracotta)' }} />
              Personalised
            </div>
          </div>

          <IllustrationBox icon={Smartphone} label="One page, two readings — click any annotation to scroll the screen, or watch it auto-tour every 30s">
            <div className="grid grid-cols-1 md:grid-cols-[380px_1fr] gap-7 items-start">
              {/* LEFT — phone (360px wide) with scrollable inner image; height matches tab column */}
              <div className="flex justify-center md:justify-start">
                <div
                  className="rounded-[24px] shadow-warm w-full max-w-[360px] relative overflow-hidden"
                  style={{
                    background: '#0F0A14',
                    border: '1.5px solid var(--ink)',
                    height: phoneHeight ? `${phoneHeight}px` : '600px',
                  }}
                >
                  <div
                    ref={landingScrollRef}
                    className="w-full h-full overflow-y-auto"
                    style={{ scrollBehavior: 'smooth' }}
                  >
                    <img
                      src={landingPageImg}
                      alt="Shots landing page anatomy"
                      className="block w-full h-auto"
                      style={{ display: 'block' }}
                      loading="lazy"
                    />
                  </div>
                  {/* Section pill overlay */}
                  <div
                    className="absolute top-2.5 left-2.5 px-2.5 py-1 rounded-full text-[9.5px] font-semibold tracking-[0.12em] uppercase pointer-events-none flex items-center gap-1.5"
                    style={{ background: 'rgba(15,10,20,0.78)', color: '#FAF6EE', backdropFilter: 'blur(8px)' }}
                  >
                    {(() => { const Ic = LANDING_TABS[landingIdx].Icon; return <Ic size={11} />; })()}
                    {LANDING_TABS[landingIdx].title.split(' / ')[0]}
                  </div>
                  {landingAuto && (
                    <div
                      className="absolute bottom-2.5 right-2.5 px-2 py-0.5 rounded-full text-[9px] font-semibold tracking-[0.16em] uppercase pointer-events-none flex items-center gap-1"
                      style={{ background: 'var(--terracotta)', color: '#FAF6EE' }}
                    >
                      <span className="block w-1.5 h-1.5 rounded-full bg-white/90 animate-pulse" /> auto
                    </div>
                  )}
                </div>
              </div>

              {/* RIGHT — annotation list (8 tabs) */}
              <div ref={landingTabsRef} className="flex flex-col gap-2.5">
                {LANDING_TABS.map((t, i) => {
                  const isOn = landingIdx === i;
                  return (
                    <button
                      key={t.n}
                      type="button"
                      onClick={() => selectLanding(i)}
                      className="text-left rounded-[12px] p-3.5 md:p-4 transition-all duration-200 flex items-start gap-3.5"
                      style={{
                        background: isOn ? 'var(--terracotta)' : 'var(--bg)',
                        color: isOn ? '#FAF6EE' : 'var(--ink)',
                        border: `1.5px solid ${isOn ? 'var(--terracotta)' : 'var(--border)'}`,
                        boxShadow: isOn ? '0 10px 22px -12px rgba(196,98,45,0.5)' : 'none',
                      }}
                    >
                      <span
                        className="shrink-0 w-10 h-10 rounded-[10px] flex items-center justify-center"
                        style={{
                          background: isOn ? 'rgba(255,255,255,0.16)' : '#FAF0E8',
                          color: isOn ? '#FAF6EE' : 'var(--terracotta-deep)',
                          border: isOn ? '1px solid rgba(255,255,255,0.22)' : '1px solid rgba(196,98,45,0.18)',
                        }}
                      >
                        <t.Icon size={20} />
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <p className="text-[13.5px] font-medium leading-tight">{t.title}</p>
                          <span
                            className="text-[9px] tracking-[0.14em] uppercase px-1.5 py-0.5 rounded-full font-semibold"
                            style={{
                              background: t.dynamic
                                ? (isOn ? 'rgba(255,255,255,0.18)' : 'rgba(196,98,45,0.16)')
                                : (isOn ? 'rgba(255,255,255,0.14)' : 'rgba(122,140,110,0.18)'),
                              color: t.dynamic
                                ? (isOn ? '#FAF6EE' : 'var(--terracotta-deep)')
                                : (isOn ? '#FAF6EE' : 'var(--sage)'),
                            }}
                          >
                            {t.dynamic ? 'Personalised' : 'Same'}
                          </span>
                        </div>
                        <p
                          className="text-[12px] leading-[1.55]"
                          style={{ color: isOn ? '#FADDCC' : 'var(--ink-soft)' }}
                        >
                          {t.sub}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </IllustrationBox>

          <P>
            What&apos;s worth noticing is the order. The page doesn&apos;t lead with personalisation — it earns its way there. By the time a
            viewer reaches the bottom three sections, they&apos;ve already told the system something about themselves just by scrolling,
            filtering, or tapping a genre tile above. The recommendation engine isn&apos;t guessing cold; it&apos;s responding to a few
            seconds of real behaviour.
          </P>

          {/* Reflect — Why this mattered */}
          <div className="reveal relative mt-12 mb-4">
            {/* Big translucent display word behind */}
            <span
              aria-hidden="true"
              className="hidden md:block absolute -top-8 -left-2 font-display italic select-none pointer-events-none"
              style={{
                fontSize: 'clamp(110px, 14vw, 170px)',
                lineHeight: 1,
                color: 'var(--ochre)',
                opacity: 0.14,
                fontWeight: 700,
                letterSpacing: '-0.04em',
              }}
            >
              Why.
            </span>

            <div
              className="relative rounded-[20px] p-7 md:p-10 overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, var(--ink) 0%, #2A1814 100%)',
                color: '#FAF6EE',
                boxShadow: '0 24px 48px -22px rgba(28,18,14,0.45)',
              }}
            >
              {/* corner ornament */}
              <span
                aria-hidden="true"
                className="absolute top-5 right-6 text-[18px] tracking-[8px]"
                style={{ color: 'var(--ochre)' }}
              >
                · · ·
              </span>

              <p className="text-[10.5px] tracking-[0.32em] uppercase mb-4 font-semibold" style={{ color: 'var(--ochre)' }}>
                Why this mattered
              </p>

              <p
                className="font-display leading-[1.25] mb-5"
                style={{ fontSize: 'clamp(24px, 3vw, 34px)', color: '#FAF6EE', fontWeight: 500 }}
              >
                Five of eight sections <em className="italic" style={{ color: 'var(--ochre)' }}>never change.</em>
              </p>

              <p className="text-[15px] md:text-[15.5px] leading-[1.75] max-w-[640px]" style={{ color: '#D8C8B4' }}>
                That wasn&apos;t a limitation of the build — it was the point. A page that personalises everything feels unpredictable. A page
                that personalises one sentence at the top, and a few rails near the bottom, feels like it&apos;s paying attention without ever
                feeling like a different product each time you open it.
              </p>

              {/* Bottom row of stats */}
              <div className="mt-7 pt-5 flex flex-wrap gap-x-9 gap-y-3" style={{ borderTop: '1px solid rgba(218,196,160,0.18)' }}>
                <div className="flex items-baseline gap-2">
                  <span className="font-display text-[28px] leading-none" style={{ color: 'var(--ochre)', fontWeight: 600 }}>5/8</span>
                  <span className="text-[11.5px] tracking-[0.12em] uppercase" style={{ color: '#B8A890' }}>same for everyone</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="font-display text-[28px] leading-none" style={{ color: 'var(--terracotta)', fontWeight: 600 }}>3/8</span>
                  <span className="text-[11.5px] tracking-[0.12em] uppercase" style={{ color: '#B8A890' }}>personalised</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="font-display text-[28px] leading-none" style={{ color: '#FAF6EE', fontWeight: 600 }}>1</span>
                  <span className="text-[11.5px] tracking-[0.12em] uppercase" style={{ color: '#B8A890' }}>page, two readings</span>
                </div>
              </div>
            </div>
          </div>
        </Chapter>

        <Ornament />

        {/* 06 — CONTINUITY */}
        <Chapter id="continuity" number="06 — Solving continuity">
          <H2>Discovery solved half the problem. <em className="italic" style={{ color: 'var(--terracotta)' }}>Then came the player.</em></H2>
          <P>
            Once a user opened a microdrama, a new behavioural problem appeared: how do you sustain attention across episodes that are each
            only a few minutes long?
          </P>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 my-7">
            <DecisionStep Icon={Film}        title="Choose a title" />
            <DecisionStep Icon={Play}        title="Choose an episode" />
            <DecisionStep Icon={RotateCcw}   title="Return to listings" />
            <DecisionStep Icon={ListChecks}  title="Choose again" />
          </div>
          <P>
            Every decision point here is a chance to leave. For long-form content that&apos;s acceptable. For microdramas, it&apos;s costly.
          </P>

          <div className="grid grid-cols-1 md:grid-cols-2 rounded-[12px] overflow-hidden my-8" style={{ border: '1px solid var(--border)', gap: 1, background: 'var(--border)' }}>
            <BAPanel
              variant="bad"
              title="Treated as isolated"
              items={[
                'Each episode is its own destination',
                'Re-enter discovery after every episode',
                'High navigation overhead',
              ]}
            />
            <BAPanel
              variant="good"
              title="Designed for momentum"
              items={[
                'Continuous viewing experience',
                'No repeated re-entry into discovery mode',
                'Focus stays on content, not interface',
              ]}
            />
          </div>

          <HighlightQuote eyebrow="Continuity insight">
            &ldquo;Once users begin watching, they shouldn&apos;t have to repeatedly re-enter discovery mode.&rdquo;
          </HighlightQuote>

          <IllustrationBox icon={Film} label="Player flow — click a tab to see the screen, video player loads by default">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_300px] gap-7 items-start">
              {/* LEFT — tabs grid (2x2 + last full width) */}
              <div>
                <div className="grid grid-cols-2 gap-3">
                  {PLAYER_TABS.map((t, i) => {
                    const isOn = playerIdx === i;
                    const isLast = i === PLAYER_TABS.length - 1;
                    return (
                      <button
                        key={t.n}
                        onClick={() => setPlayerIdx(i)}
                        className={`flex flex-col items-start justify-between gap-3 rounded-[14px] p-4 transition-all duration-200 text-left aspect-square ${isLast ? 'col-span-2 aspect-auto min-h-[88px] flex-row items-center' : ''}`}
                        style={{
                          background: isOn ? 'var(--terracotta)' : 'var(--bg)',
                          color: isOn ? '#FAF6EE' : 'var(--ink)',
                          border: `1.5px solid ${isOn ? 'var(--terracotta)' : 'var(--border)'}`,
                          boxShadow: isOn ? '0 10px 24px -10px rgba(196,98,45,0.5)' : 'none',
                        }}
                      >
                        {isLast ? (
                          <>
                            <t.Icon size={32} style={{ color: isOn ? '#FAF6EE' : 'var(--terracotta)' }} />
                            <div className="flex-1">
                              <span className="block text-[10px] tracking-[0.22em] uppercase mb-1" style={{ color: isOn ? '#FADDCC' : 'var(--sage)' }}>{t.n}</span>
                              <span className="block text-[14px] font-medium leading-snug">{t.title}</span>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="flex items-center justify-between w-full">
                              <span className="text-[10px] tracking-[0.22em] uppercase" style={{ color: isOn ? '#FADDCC' : 'var(--sage)' }}>{t.n}</span>
                              <t.Icon size={32} />
                            </div>
                            <span className="text-[14px] font-medium leading-snug">{t.title}</span>
                          </>
                        )}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[12px] mt-5 leading-[1.55] italic" style={{ color: 'var(--ink-soft)' }}>
                  {PLAYER_TABS[playerIdx].sub}
                </p>
              </div>

              {/* RIGHT — phone mockup */}
              <div className="flex justify-center md:justify-end">
                <div
                  className="overflow-hidden rounded-[22px] shadow-warm w-full max-w-[260px]"
                  style={{ aspectRatio: '360 / 805', background: '#0F0A14', border: '1.5px solid var(--ink)' }}
                >
                  <img
                    key={PLAYER_TABS[playerIdx].image}
                    src={PLAYER_TABS[playerIdx].image}
                    alt={PLAYER_TABS[playerIdx].title}
                    className="block w-full h-full object-cover transition-opacity duration-500"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </IllustrationBox>

          <P>
            Continuous viewing can improve engagement — but pushed too far, it starts to feel automated rather than guided. The player needed to
            encourage progression while still preserving the sense that the user, not the app, was in control.
          </P>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 my-6">
            <ConstraintCard Icon={TrendingUp} title="Encourage progression" sub="momentum without forcing it" />
            <ConstraintCard Icon={Compass}    title="Maintain orientation"  sub="users always know where they are" />
            <ConstraintCard Icon={Bird}       title="Preserve autonomy"     sub="exit and control stay one tap away" />
          </div>
        </Chapter>

        {/* 07 — FRAGMENTS OF TIME */}
        <Chapter id="fragments" number="07 — Designed for fragments of time">
          <H2>Microdramas live in <em className="italic" style={{ color: 'var(--terracotta)' }}>spare minutes.</em></H2>
          <P>
            This kind of content gets consumed while commuting, waiting, multitasking — in the gaps of a day, not a dedicated sitting. Every
            interaction was evaluated against that reality.
          </P>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 my-7">
            <FragmentCard Icon={Bus}       title="Commuting" />
            <FragmentCard Icon={Hourglass} title="Waiting" />
            <FragmentCard Icon={Layers}    title="Multitasking" />
            <FragmentCard Icon={Coffee}    title="Filling spare minutes" />
          </div>
          <Insight icon={Zap}>
            Optimised for <strong className="text-white font-medium">quick entry, immediate understanding, effortless continuation, and easy
            exit</strong> — four tests every interaction had to pass.
          </Insight>

          <IllustrationBox icon={Smartphone} label="Player screens — built for short attention windows">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <PhoneScreen image={fullPlayerImg}  Icon={Film}    title="Full-bleed vertical player" sub="Swipe interaction, like/share/episodes rail" />
              <PhoneScreen image={videoPlayerImg} Icon={Play}    title="Playback controls"          sub="Play, pause, skip — minimal and familiar" />
              <PhoneScreen image={adInPlayerImg}  Icon={Target}  title="Ad placement in-player"     sub="Partner offer unit between episodes" />
            </div>
          </IllustrationBox>
        </Chapter>

        <Ornament />

        {/* 08 — OUTCOME */}
        <Chapter id="outcome" number="08 — Outcome">
          <H2>From intentional search <em className="italic" style={{ color: 'var(--terracotta)' }}>to natural discovery.</em></H2>
          <P>
            Instead of requiring users to actively seek out a new content category, the experience let discovery happen through journeys they
            were already on.
          </P>

          {/* Funnel */}
          <div className="reveal my-8">
            <FunnelFlow
              activeIdx={funnelIdx}
              steps={[
                { Icon: Search,     label: 'Discovery' },
                { Icon: Sparkles,   label: 'Curiosity' },
                { Icon: Play,       label: 'Viewing' },
                { Icon: RotateCcw,  label: 'Continued engagement' },
              ]}
            />
            <p className="text-[12.5px] mt-5 text-center italic" style={{ color: 'var(--ink-soft)' }}>
              No new behaviour had to be learned upfront — it emerged from familiar ones.
            </p>
          </div>

          {/* Learnings */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 mt-8">
            <LearningCard n="01">
              New features succeed when they <strong className="font-semibold" style={{ color: 'var(--ink)' }}>fit into existing behaviours</strong>,
              not when they ask users to form new ones first.
            </LearningCard>
            <LearningCard n="02">
              <strong className="font-semibold" style={{ color: 'var(--ink)' }}>Visibility alone doesn&apos;t create adoption.</strong> Relevance and timing do the real work.
            </LearningCard>
            <LearningCard n="03">
              Every extra decision point is a <strong className="font-semibold" style={{ color: 'var(--ink)' }}>drop-off risk</strong> — for short-form content, this cost compounds fast.
            </LearningCard>
            <LearningCard n="04">
              Discovery and consumption are <strong className="font-semibold" style={{ color: 'var(--ink)' }}>one connected system</strong>, not two separate design problems.
            </LearningCard>
          </div>
        </Chapter>

        <Ornament />

        {/* 09 — WHAT I'D EXPLORE NEXT */}
        <Chapter id="explore-next" number="09 — What I'd explore next">
          <H2>Where this could go <em className="italic" style={{ color: 'var(--terracotta)' }}>from here.</em></H2>
          <P>
            A network of discovery surfaces and a continuity-first player created the foundation. The next set of moves would push the
            system from reactive to anticipatory.
          </P>
          <div className="grid grid-cols-1 gap-3.5 mt-8">
            <NextRow Icon={Target}    title="Personalised recommendations"        sub="Surface titles based on watch history, language, and time-of-day signals." />
            <NextRow Icon={MapPin}    title="Behaviour-based entry optimisation"  sub="Promote the entry point most likely to convert for that user, on that day." />
            <NextRow Icon={Compass}   title="Cross-platform continuation"         sub="Pick up an episode on the TPMA mobile app where you left off on the connected TV." />
            <NextRow Icon={Sparkles}  title="Predictive sequencing"               sub="Stitch episodes from different titles into a personalised, never-ending micro-stream." />
          </div>
        </Chapter>

        {/* CLOSING */}
        <section className="max-w-3xl mx-auto px-6 py-16 text-center" style={{ borderTop: '1px solid var(--border)' }}>
          <p className="font-display italic leading-[1.5] mb-8 mx-auto max-w-[640px]" style={{ fontSize: 'clamp(20px, 2.2vw, 26px)', color: 'var(--ink-soft)' }}>
            &ldquo;Users rarely adopt a feature because it exists. They adopt it when it fits naturally into how they already behave.
            Successful product experiences don&apos;t just deliver content — they reshape behaviour.&rdquo;
          </p>
          <div className="flex flex-wrap justify-center gap-3.5">
            <a
              href="https://ritikaghosh.framer.website/"
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-[13px] transition-colors duration-300"
              style={{ background: 'var(--terracotta)', color: '#FAF6EE', border: '1.5px solid var(--terracotta)' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#A4501F'; e.currentTarget.style.borderColor = '#A4501F'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--terracotta)'; e.currentTarget.style.borderColor = 'var(--terracotta)'; }}
            >
              View actual prototype <ArrowUpRight size={15} />
            </a>
            <Link
              to="/#work"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-[13px] transition-colors duration-300"
              style={{ background: 'transparent', color: 'var(--ink)', border: '1.5px solid var(--border)' }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--ink-soft)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; }}
            >
              All case studies <ArrowRight size={15} />
            </Link>
          </div>
        </section>

        {/* Next / back */}
        <div className="max-w-3xl mx-auto px-6 mt-12 pt-10 flex flex-wrap items-center justify-between gap-6" style={{ borderTop: '1px solid var(--border)' }}>
          <Link to="/#work" className="inline-flex items-center gap-2 text-[13px] tracking-[0.12em] uppercase link-underline" style={{ color: 'var(--ink)' }}>
            <ArrowLeft size={15} /> Back to all work
          </Link>
          {nextProject && (
            <Link to={`/case/${nextProject.slug}`} className="group inline-flex items-center gap-3 rounded-2xl px-5 py-4 transition-all duration-300" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
              <span className="text-right">
                <span className="block text-[11px] tracking-[0.18em] uppercase" style={{ color: 'var(--sage)' }}>Next case study</span>
                <span className="block font-display text-[18px]" style={{ color: 'var(--ink)' }}>{nextProject.title}</span>
              </span>
              <ArrowRight size={18} style={{ color: 'var(--terracotta)' }} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          )}
        </div>
      </article>

      <Footer />
    </div>
  );
}

/* ----- Shared blocks ----- */
function MetaPill({ children, accent = false }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[12px] font-medium" style={{ background: accent ? 'var(--terracotta)' : 'var(--surface)', color: accent ? '#FAF6EE' : 'var(--ink-soft)', border: `1px solid ${accent ? 'var(--terracotta)' : 'var(--border)'}` }}>
      {children}
    </span>
  );
}
function Chapter({ id, number, children }) {
  return (
    <section id={id} className="max-w-3xl mx-auto px-6 mb-24 scroll-mt-24">
      {number && <p className="reveal text-[11px] tracking-[0.22em] uppercase mb-3" style={{ color: 'var(--ink-soft)' }}>{number}</p>}
      {children}
    </section>
  );
}
function H2({ children }) {
  return (<h2 className="reveal font-display leading-[1.2] mb-6" style={{ fontSize: 'clamp(26px, 3vw, 36px)', color: 'var(--ink)', fontWeight: 600 }}>{children}</h2>);
}
function P({ children }) { return (<p className="reveal text-[15.5px] leading-[1.8] mb-4" style={{ color: 'var(--ink-soft)' }}>{children}</p>); }
function PullQuote({ children, eyebrow }) {
  return (
    <blockquote className="reveal my-8 px-6 py-5 rounded-r-[8px]" style={{ borderLeft: '3px solid var(--terracotta)', background: '#FAF0E8' }}>
      {eyebrow && <p className="text-[10px] tracking-[0.22em] uppercase mb-2" style={{ color: 'var(--terracotta-deep)' }}>{eyebrow}</p>}
      <p className="font-display italic text-[19px] leading-[1.55]" style={{ color: 'var(--ink)' }}>{children}</p>
    </blockquote>
  );
}
function IllustrationBox({ icon: Icon, label, children }) {
  return (
    <div className="reveal my-9 rounded-[16px] overflow-hidden" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
      <div className="flex items-center gap-2 px-5 py-3.5" style={{ borderBottom: '1px solid var(--border)' }}>
        {Icon && <Icon size={14} style={{ color: 'var(--terracotta)' }} />}
        <span className="text-[11px] tracking-[0.14em] uppercase" style={{ color: 'var(--ink-soft)' }}>{label}</span>
      </div>
      <div className="px-6 py-7">{children}</div>
    </div>
  );
}
function Insight({ icon: Icon, children }) {
  return (
    <div className="reveal flex items-start gap-3.5 p-5 rounded-[12px] my-7" style={{ background: 'var(--ink)' }}>
      <Icon size={20} style={{ color: 'var(--ochre)', flexShrink: 0, marginTop: 2 }} />
      <p className="text-[13.5px] leading-[1.65]" style={{ color: '#D4C8B8' }}>{children}</p>
    </div>
  );
}
function Ornament() { return (<div className="text-center my-12 tracking-[8px] text-[20px]" style={{ color: 'var(--ochre)' }}>· · ·</div>); }
function LearningCard({ n, children }) {
  return (
    <div className="reveal rounded-[12px] p-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
      <p className="font-display leading-none mb-2.5" style={{ fontSize: 28, color: 'var(--ochre)', fontWeight: 600 }}>{n}</p>
      <p className="text-[13.5px] leading-[1.6]" style={{ color: 'var(--ink-soft)' }}>{children}</p>
    </div>
  );
}
function Placeholder({ Icon, title, children }) {
  return (
    <div className="w-full text-center flex flex-col items-center gap-2.5 px-5 py-8 rounded-[12px]" style={{ background: 'linear-gradient(135deg, #F0E8DC 0%, #E8DDD0 100%)', border: '1.5px dashed var(--border)' }}>
      <span className="w-9 h-9 rounded-[10px] flex items-center justify-center" style={{ background: '#F2C4A2' }}>
        <Icon size={16} style={{ color: 'var(--terracotta-deep)' }} />
      </span>
      <span className="font-medium text-[12.5px]" style={{ color: 'var(--ink)' }}>{title}</span>
      <span className="text-[11.5px] leading-snug max-w-[280px]" style={{ color: 'var(--ink-soft)' }}>{children}</span>
    </div>
  );
}
function PhoneScreen({ image, Icon, title, sub }) {
  return (
    <div className="flex flex-col items-center gap-3">
      <div
        className="overflow-hidden rounded-[20px] shadow-warm w-full max-w-[220px]"
        style={{ aspectRatio: '360 / 805', background: '#0F0A14', border: '1.5px solid var(--ink)' }}
      >
        <img src={image} alt={title} className="block w-full h-full object-cover" loading="lazy" />
      </div>
      <div className="text-center px-1">
        <div className="flex items-center justify-center gap-1.5 mb-1">
          <Icon size={14} style={{ color: 'var(--terracotta)' }} />
          <p className="text-[13px] font-medium" style={{ color: 'var(--ink)' }}>{title}</p>
        </div>
        <p className="text-[11.5px] leading-[1.55]" style={{ color: 'var(--ink-soft)' }}>{sub}</p>
      </div>
    </div>
  );
}

/* Page-specific blocks */
function IntentCard({ Icon, title }) {
  return (
    <div className="flex items-center gap-3 rounded-[12px] px-4 py-3.5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
      <span className="w-9 h-9 rounded-[10px] flex items-center justify-center" style={{ background: '#FAF0E8' }}>
        <Icon size={17} style={{ color: 'var(--terracotta-deep)' }} />
      </span>
      <p className="text-[13.5px] font-medium" style={{ color: 'var(--ink)' }}>{title}</p>
    </div>
  );
}
function SurfaceCard({ Icon, title }) {
  return (
    <div
      className="flex flex-col items-center justify-center text-center gap-2 rounded-[12px] px-2.5 py-4 h-full min-h-[110px]"
      style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}
    >
      <span className="w-9 h-9 rounded-[10px] flex items-center justify-center shrink-0" style={{ background: '#FAF0E8' }}>
        <Icon size={17} style={{ color: 'var(--terracotta-deep)' }} />
      </span>
      <p className="text-[12px] font-medium leading-[1.3]" style={{ color: 'var(--ink)' }}>{title}</p>
    </div>
  );
}
function HighlightQuote({ eyebrow, children }) {
  return (
    <div
      className="reveal my-10 rounded-[18px] p-7 md:p-9 text-center relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, var(--terracotta) 0%, var(--terracotta-deep) 100%)',
        boxShadow: '0 22px 48px -20px rgba(196,98,45,0.55)',
      }}
    >
      <span className="absolute top-3 left-5 font-display text-[80px] leading-none opacity-30 select-none" style={{ color: '#FAF6EE' }}>&ldquo;</span>
      <span className="absolute bottom-0 right-5 font-display text-[80px] leading-none opacity-30 select-none" style={{ color: '#FAF6EE' }}>&rdquo;</span>
      {eyebrow && (
        <p className="text-[10.5px] tracking-[0.28em] uppercase mb-3 font-semibold relative" style={{ color: '#FADDCC' }}>{eyebrow}</p>
      )}
      <p
        className="font-display italic leading-[1.4] relative"
        style={{ fontSize: 'clamp(22px, 3vw, 30px)', color: '#FAF6EE', fontWeight: 500 }}
      >
        {children}
      </p>
    </div>
  );
}
function PrincipleCard({ Icon, title, sub }) {
  return (
    <div className="rounded-[14px] p-6 text-center flex flex-col items-center" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
      <Icon size={26} style={{ color: 'var(--terracotta)' }} className="mb-3" />
      <p className="font-display text-[22px] leading-none mb-2" style={{ color: 'var(--ink)', fontWeight: 600 }}>{title}</p>
      <p className="text-[13px] leading-[1.55] max-w-[200px]" style={{ color: 'var(--ink-soft)' }}>{sub}</p>
    </div>
  );
}
function FromToBox({ eyebrow, text, tone }) {
  const isAccent = tone === 'accent';
  return (
    <div
      className="rounded-[16px] p-7 transition-all duration-300"
      style={{
        background: isAccent ? 'var(--terracotta)' : 'var(--surface)',
        border: `2px ${isAccent ? 'solid var(--terracotta)' : 'dashed var(--border)'}`,
        boxShadow: isAccent ? '0 18px 38px -16px rgba(196,98,45,0.55)' : '0 4px 12px -6px rgba(90,60,30,0.10)',
      }}
    >
      <p
        className="text-[10.5px] tracking-[0.28em] uppercase mb-3 font-semibold"
        style={{ color: isAccent ? '#FADDCC' : 'var(--sage)' }}
      >
        {eyebrow}
      </p>
      <p
        className="font-display italic leading-[1.35]"
        style={{
          fontSize: 'clamp(18px, 2.2vw, 22px)',
          color: isAccent ? '#FAF6EE' : 'var(--ink)',
          fontWeight: 500,
        }}
      >
        {text}
      </p>
    </div>
  );
}
function OptionCard({ n, Icon, title, pros, cons, winner = false, tag }) {
  return (
    <div
      className="rounded-[16px] p-5 md:p-6 grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr] gap-5 md:gap-7 items-start"
      style={{
        background: winner ? '#FAF0E8' : 'var(--surface)',
        border: `1.5px solid ${winner ? 'var(--terracotta)' : 'var(--border)'}`,
        boxShadow: winner ? '0 14px 32px -16px rgba(196,98,45,0.45)' : 'none',
      }}
    >
      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] tracking-[0.22em] uppercase" style={{ color: 'var(--sage)' }}>{n}</p>
          {tag && (
            <span className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{ background: 'var(--terracotta)', color: '#FAF6EE' }}>{tag}</span>
          )}
        </div>
        <div className="flex items-center gap-2.5 mb-1">
          <Icon size={20} style={{ color: 'var(--terracotta)' }} />
          <p className="font-display text-[20px] leading-tight" style={{ color: 'var(--ink)', fontWeight: 600 }}>{title}</p>
        </div>
      </div>
      <ListSection label="Pros" items={pros} tone="good" />
      <ListSection label="Cons" items={cons} tone="bad" />
    </div>
  );
}
function ListSection({ label, items, tone }) {
  const isGood = tone === 'good';
  return (
    <div className="mt-2">
      <p className="text-[10px] tracking-[0.16em] uppercase mb-1.5" style={{ color: isGood ? 'var(--sage)' : '#8A7A60' }}>{label}</p>
      <ul className="space-y-1.5">
        {items.map((it) => (
          <li key={it} className="flex items-start gap-1.5 text-[12.5px] leading-[1.5]" style={{ color: 'var(--ink-soft)' }}>
            <span className="shrink-0 mt-0.5" style={{ color: isGood ? 'var(--sage)' : '#8A7A60' }}>{isGood ? <Check size={11} /> : <X size={11} />}</span>
            {it}
          </li>
        ))}
      </ul>
    </div>
  );
}
function DecisionStep({ Icon, title }) {
  return (
    <div className="rounded-[12px] p-3.5 text-center" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
      <Icon size={20} className="mx-auto mb-1.5" style={{ color: 'var(--terracotta)' }} />
      <p className="text-[12px] font-medium leading-snug" style={{ color: 'var(--ink)' }}>{title}</p>
    </div>
  );
}
function BAPanel({ variant, title, items }) {
  const isGood = variant === 'good';
  const accent = isGood ? 'var(--sage)' : '#8A7A60';
  return (
    <div
      className="p-5 transition-all duration-300"
      style={{
        background: isGood ? 'rgba(122,140,110,0.14)' : '#EDE8E0',
        boxShadow: isGood ? 'inset 4px 0 0 var(--sage), 0 10px 22px -14px rgba(122,140,110,0.5)' : 'none',
      }}
    >
      <div className="flex items-center gap-1.5 mb-3 text-[10px] tracking-[0.14em] uppercase font-semibold" style={{ color: isGood ? 'var(--sage)' : accent }}>
        {isGood ? <Check size={12} /> : <X size={12} />} {title}
      </div>
      <ul className="space-y-2">
        {items.map((it) => (
          <li key={it} className="flex items-start gap-2 text-[13px] leading-[1.5]" style={{ color: isGood ? 'var(--ink)' : 'var(--ink-soft)' }}>
            <span className="shrink-0 mt-0.5" style={{ color: isGood ? 'var(--sage)' : accent }}>{isGood ? '→' : '—'}</span>{it}
          </li>
        ))}
      </ul>
    </div>
  );
}
function ConstraintCard({ Icon, title, sub }) {
  return (
    <div className="rounded-[12px] p-4 text-center" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
      <Icon size={20} className="mx-auto mb-2" style={{ color: 'var(--terracotta)' }} />
      <p className="text-[12.5px] font-medium mb-1" style={{ color: 'var(--ink)' }}>{title}</p>
      <p className="text-[11.5px] leading-[1.5]" style={{ color: 'var(--ink-soft)' }}>{sub}</p>
    </div>
  );
}
function FragmentCard({ Icon, title }) {
  return (
    <div className="rounded-[14px] p-5 text-center" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
      <Icon size={22} className="mx-auto mb-2" style={{ color: 'var(--terracotta)' }} />
      <p className="text-[12.5px] font-medium" style={{ color: 'var(--ink)' }}>{title}</p>
    </div>
  );
}
function NextRow({ Icon, title, sub }) {
  return (
    <div
      className="reveal flex items-center gap-5 md:gap-7 rounded-[16px] px-5 md:px-7 py-5 md:py-6 transition-all duration-300"
      style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}
    >
      <span
        className="w-16 h-16 md:w-20 md:h-20 rounded-[16px] flex items-center justify-center shrink-0"
        style={{ background: '#FAF0E8', border: '1.5px solid rgba(196,98,45,0.25)' }}
      >
        <Icon size={36} style={{ color: 'var(--terracotta-deep)' }} />
      </span>
      <div className="flex-1 min-w-0">
        <p className="font-display text-[18px] md:text-[20px] leading-tight mb-1.5" style={{ color: 'var(--ink)', fontWeight: 600 }}>{title}</p>
        <p className="text-[13px] md:text-[14px] leading-[1.55]" style={{ color: 'var(--ink-soft)' }}>{sub}</p>
      </div>
    </div>
  );
}
function FunnelFlow({ steps, activeIdx = 0 }) {
  return (
    <div className="flex flex-col md:flex-row items-stretch md:items-center gap-2.5">
      {steps.map((s, i) => {
        const isOn = i <= activeIdx;
        const isCurrent = i === activeIdx;
        return (
          <div key={s.label} className="flex flex-col md:flex-row md:flex-1 items-center gap-2.5">
            <div
              className="w-full md:flex-1 rounded-[12px] py-5 px-3 min-h-[110px] flex flex-col items-center justify-center gap-1.5 transition-all duration-500"
              style={{
                background: isOn ? 'var(--terracotta)' : 'var(--surface)',
                border: `1px solid ${isOn ? 'var(--terracotta)' : 'var(--border)'}`,
                color: isOn ? '#FAF6EE' : 'var(--ink)',
                boxShadow: isCurrent ? '0 14px 28px -12px rgba(196,98,45,0.55)' : 'none',
                transform: isCurrent ? 'translateY(-2px)' : 'translateY(0)',
              }}
            >
              <s.Icon size={22} style={{ color: isOn ? '#FAF6EE' : 'var(--terracotta)' }} />
              <p className="text-[12.5px] font-medium text-center leading-tight">{s.label}</p>
            </div>
            {i < steps.length - 1 && (
              <div
                className="shrink-0 transition-colors duration-500"
                style={{ color: i < activeIdx ? 'var(--terracotta)' : 'var(--border)' }}
              >
                <span className="block md:hidden"><ArrowRight size={18} className="rotate-90" /></span>
                <span className="hidden md:block"><ArrowRight size={18} /></span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
